import React, { Fragment, useEffect, useMemo, useState } from "react";
import { backend, API_URL } from "../../../config";
import {
  CssBaseline,
  Grid,
  IconButton,
  Typography,
  Pagination,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Tooltip,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";

import {
  Dashboard as DashboardIcon,
  TableRows as TableRowsIcon,
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon,
} from "@mui/icons-material";

import { makeStyles } from "@mui/styles";

import { Link, useHistory } from "react-router-dom";

// import CategorySidebar from "../components/CategorySidebar/CategorySidebar";
import CardItem from "../../../components/Cards/Items/Items";
import ItemDialog from "../../../components/Dialogs/Items/Items";

import { useSelector } from "react-redux";

const columns = [
  { id: "upc", editable: "/product", editablePk: "upc", label: "UPC" },
  {
    id: "url",
    label: "Image",
  },
  // { id: "upc", label: "Code" },
  {
    id: "name",
    label: "Name",
  },
  {
    id: "display_unit_price",
    label: "Unit",
  },
  {
    id: "display_case_price",
    label: "Case",
  },
  {
    id: "stock_level",
    label: "Inventory stock",
  },
  {
    id: "combined_stock_level",
    label: "Combined stock",
  },
];

const imageTypes = [".jpg", ".jpeg", ".png"];

const useStyles = makeStyles((theme) => ({
  container: {
    minHeight: 440,
    maxHeight: "75vh",
  },
  tableBody: {
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

export const TableBase = (props) => {
  const { columns, items, size = null } = props;

  const classes = useStyles();
  const currentUserStore = useSelector((state) => state.current_user);

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState(null);
  const [openItemDialog, setOpenItemDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);

    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });

    return stabilizedThis.map((el) => el[0]);
  };

  const cellFormat = (value, row, column, rowIndex = null) => {
    switch (typeof value) {
      case "string":
        const dateVal = new Date(value);
        const containsLetter = value.match(/([A-Za-z])/g);
        if (
          !isNaN(dateVal.getTime()) &&
          containsLetter &&
          (!containsLetter.length ||
            (value.includes("T") && value.includes(":")))
        ) {
          return dateVal.toISOString().split("T")[0];
        } else if (
          value.indexOf("https://") === 0 ||
          value.indexOf("http://") === 0
        ) {
          const isImg = imageTypes.some((itype) =>
            value.toLocaleLowerCase().indexOf(itype),
          );

          if (!isImg) {
            return value;
          }

          const displayImageComp = (
            <Tooltip title="View image">
              <IconButton
                aria-label="view image"
                onClick={() => {
                  setSelectedItem(row);
                  setOpenItemDialog(true);
                }}
              >
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
          );

          return displayImageComp;
        } else {
          return value;
        }
      case "number":
        if (column.id.includes("price") || column.id.includes("total")) {
          return "$" + value.toFixed(2);
        } else {
          return value;
        }
      case "boolean":
        return value.toString();
      case "object":
        if (value === null) return "N/A";

        if (column.hasOwnProperty(column.innerId)) {
          if (!value[column.innerId]) return "N/A";

          return value[column.innerId];
        }

        return value.hasOwnProperty(column.id) ? value[column.id] : "N/A";
      default:
        return value;
    }
  };

  const EnhancedTableHead = (props) => {
    const { order, orderBy, onRequestSort } = props;

    const createSortHandler = (property) => (event) => {
      onRequestSort(event, property);
    };

    return (
      <TableHead>
        <TableRow>
          {columns
            .filter((headCell) => {
              let visible = true;

              if (headCell.hasOwnProperty("visibility_deny_rid")) {
                visible = !headCell.visibility_deny_rid.includes(
                  currentUserStore.role_id,
                );
              }

              if (headCell.hasOwnProperty("visibility_allow_rid")) {
                visible = headCell.visibility_allow_rid.includes(
                  currentUserStore.role_id,
                );
              }

              return visible;
            })
            .map((headCell, index) => (
              <TableCell
                key={index}
                align={headCell.numeric ? "right" : "left"}
                sortDirection={orderBy === headCell.id ? order : false}
                style={{
                  width: ["url", "_acceptable_substitutions"].includes(
                    headCell.id,
                  )
                    ? 40
                    : "inherit",
                  padding: ["url", "_acceptable_substitutions"].includes(
                    headCell.id,
                  )
                    ? 0
                    : "auto",
                }}
              >
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                </TableSortLabel>
              </TableCell>
            ))}
        </TableRow>
      </TableHead>
    );
  };

  return (
    <>
      <TableContainer
        className={classes.container}
        style={{ minHeight: size === null ? "inherit" : size }}
      >
        <Table stickyHeader>
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            {stableSort(items, getComparator(order, orderBy)).map(
              (row, rowIndex) => (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={rowIndex}
                  className={`${classes.tableBody}${
                    row.active === false ? " iInactive" : ""
                  }`}
                >
                  {columns
                    .filter((headCell) => {
                      let visible = true;

                      if (headCell.hasOwnProperty("visibility_deny_rid")) {
                        visible = !headCell.visibility_deny_rid.includes(
                          currentUserStore.role_id,
                        );
                      }

                      if (headCell.hasOwnProperty("visibility_allow_rid")) {
                        visible = headCell.visibility_allow_rid.includes(
                          currentUserStore.role_id,
                        );
                      }

                      return visible;
                    })
                    .map((column, columnIndex) => (
                      <TableCell
                        key={rowIndex + "-" + columnIndex}
                        style={{
                          width: ["url", "_acceptable_substitutions"].includes(
                            column.id,
                          )
                            ? 40
                            : "inherit",
                          padding: [
                            "url",
                            "_acceptable_substitutions",
                          ].includes(column.id)
                            ? 0
                            : "auto",
                        }}
                      >
                        {column.editable && column.editablePk ? (
                          <Link
                            to={{
                              pathname: `${column.editable}/${
                                row[column.editablePk]
                              }`,
                              state: { data: row },
                            }}
                            {...(column?.target
                              ? {
                                  target: column.target,
                                }
                              : undefined)}
                          >
                            {row[column.id]}
                          </Link>
                        ) : (
                          cellFormat(row[column.id], row, column, rowIndex)
                        )}
                      </TableCell>
                    ))}
                </TableRow>
              ),
            )}
            {!items.length && (
              <TableRow style={{ height: 53 }}>
                <TableCell colSpan={columns.length} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <ItemDialog
        data={selectedItem}
        openItemDialog={openItemDialog}
        setOpenItemDialog={setOpenItemDialog}
      />
    </>
  );
};

const Products = (props) => {
  const history = useHistory();
  const cartStore = useSelector((state) => state.cart);
  const customerStore = useSelector((state) => state.customer);

  const [itemsPerPage, setItemsPerPage] = useState(100); // todo: make it UI customizable
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [products, setProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [total, setTotal] = useState(0.0);
  const [view, setView] = useState(1);
  const [openItemDialog, setOpenItemDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  const query = useMemo(() => {
    setProducts([]);
    setProductsLoaded(false);

    return new URLSearchParams(props?.location?.search);
  }, [props]);

  useEffect(() => {
    setSearchValue(query.get("search") || "");
  }, [query]);

  useMemo(async () => {
    const querySearch = query.get("search");
    const queryGroup = query.getAll("group");
    const queryDepartment = query.getAll("department");
    const queryCategory = query.get("category");

    let urlParam = "";

    urlParam =
      urlParam +
      (page > 1
        ? `${urlParam === "" ? "" : "&"}limit=${itemsPerPage}&offset=${
            (page - 1) * itemsPerPage
          }`
        : "");

    queryGroup.forEach((group) => {
      urlParam =
        urlParam + (group ? `${urlParam === "" ? "" : "&"}group=${group}` : "");
    });

    queryDepartment.forEach((department) => {
      urlParam =
        urlParam +
        (department
          ? `${urlParam === "" ? "" : "&"}department=${department}`
          : "");
    });

    urlParam =
      urlParam +
      (queryCategory
        ? `${urlParam === "" ? "" : "&"}category=${queryCategory}`
        : "");

    urlParam =
      urlParam +
      (querySearch ? `${urlParam === "" ? "" : "&"}search=${querySearch}` : "");

    await backend.get(`${API_URL}productsearch?${urlParam}`).then(({ data }) => {
      const dataMap = data.results
        .map((item) => ({
          ...item,
          unit_in_cart: 0,
          case_in_cart: 0,
          added_in_cart: false,
          added_through_sub: false,
        }));

      const pageCountCalc = data.count / itemsPerPage;
      setPageCount(+pageCountCalc.toFixed());
      setProducts(dataMap);
      setProductsLoaded(true);
    });
  }, [query, page, itemsPerPage]);

  // todo: remove when making itemsPerPage UI customizable
  useEffect(() => {
    if (!itemsPerPage) {
      setItemsPerPage(100);
    }
  }, [itemsPerPage]);

  const handlePaginationChange = (e, value) => {
    setProducts([]);
    setProductsLoaded(false);
    setPage(value);
  };

  const handleClickCardView = () => {
    setView(1);
  };

  const handleClickTableView = () => {
    setView(2);
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const currentCategory = query.get("category");
    const params = new URLSearchParams();

    if (currentCategory) {
      params.set("category", currentCategory);
    }
    if (searchValue.trim()) {
      params.set("search", searchValue.trim());
    }

    history.push(`/products?${params.toString()}`);
  };

  return (
    <Fragment>
      <CssBaseline />
      <Grid container spacing={2}>
        {/* <Grid item xs={12} sm={3} md={3} lg={2} xl={1.5}>
          <CategorySidebar selectedCategoryId={query.get("category")} />
        </Grid>
        <Grid item xs={12} sm={9} md={9} lg={10} xl={10.5}> */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={true} sm={true}>
              <Typography variant="h6" component="div">
                Products
                {query.get("search")
                  ? ` found for: "${query.get("search")}"`
                  : ""}
              </Typography>
            </Grid>
            <Grid item xs={"auto"}>
              <IconButton onClick={handleClickCardView}>
                <DashboardIcon className={view === 1 ? "activeView" : ""} />
              </IconButton>
              <IconButton onClick={handleClickTableView}>
                <TableRowsIcon className={view === 2 ? "activeView" : ""} />
              </IconButton>
            </Grid>
            <Grid item xs={12}>
              <form onSubmit={handleSearchSubmit}>
                <TextField
                  fullWidth
                  placeholder={
                    query.get("category")
                      ? "Search within this category..."
                      : "Search products..."
                  }
                  value={searchValue}
                  onChange={handleSearchChange}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </form>
            </Grid>
            {products.length ? (
              <>
                {pageCount > 1 ? (
                  <Grid item xs={12} sm={12} textAlign="right">
                    <Pagination
                      count={pageCount}
                      page={page}
                      onChange={handlePaginationChange}
                      style={{ display: "inline-block" }}
                    />
                  </Grid>
                ) : (
                  <></>
                )}
                {view === 1 ? (
                  products.map((item, index) => (
                    <Grid item xs={12} sm={4} md={3} lg={3} xl={2} key={index}>
                      <CardItem
                        {...props}
                        type={1}
                        data={item}
                        total={total}
                        setTotal={setTotal}
                        setSelectedItem={setSelectedItem}
                        setOpenItemDialog={setOpenItemDialog}
                        reloadable={true}
                        cartInfo={
                          customerStore !== null
                            ? cartStore[customerStore.id] &&
                              cartStore[customerStore.id].products.find(
                                (product) => product.upc === item.upc,
                              )
                            : undefined
                        }
                        storeAddons={true}
                      />
                    </Grid>
                  ))
                ) : (
                  <TableBase columns={columns} items={products} />
                )}
              </>
            ) : (
              <Grid item xs={12} align="center">
                {!productsLoaded && <CircularProgress />}
                <Typography variant="h6">
                  {!productsLoaded
                    ? "Loading products..."
                    : "No products found with the provided search criteria"}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
      <ItemDialog
        data={selectedItem}
        openItemDialog={openItemDialog}
        setOpenItemDialog={setOpenItemDialog}
      />
    </Fragment>
  );
};

export default Products;
